import { z } from 'zod';
import { pageSchema, timestampSchema, cursorQuerySchema } from './admin.js';

/**
 * Security events of the audit log (docs/03 audit_log, ADM-05) with their category. Extend the
 * catalog instead of using free text; the web app needs a German text for every action.
 */
export const auditActionCatalog = {
  'auth.login_succeeded': 'auth',
  'auth.login_failed': 'auth',
  'auth.login_blocked': 'auth',
  'auth.logout': 'auth',
  'auth.password_reset_requested': 'auth',
  'auth.password_reset_completed': 'auth',
  'auth.two_factor_enabled': 'auth',
  'auth.two_factor_reset': 'auth',
  'auth.backup_codes_regenerated': 'auth',
  'auth.sessions_revoked': 'auth',
  'auth.entra_linked': 'auth',
  'auth.entra_rejected': 'auth',
  'user.invited': 'users',
  'user.invitation_accepted': 'users',
  'user.provisioned': 'users',
  'user.created_internal': 'users',
  'user.entra_profile_updated': 'users',
  'user.deactivated': 'users',
  'user.activated': 'users',
  'user.avatar_removed': 'users',
  'user.break_glass_created': 'users',
  'user.break_glass_reset': 'users',
  'user.global_roles_changed': 'rights',
  'role.created': 'rights',
  'role.updated': 'rights',
  'role.deleted': 'rights',
  'project.member_added': 'rights',
  'project.member_role_changed': 'rights',
  'project.member_removed': 'rights',
  'organization.created': 'organizations',
  'organization.updated': 'organizations',
  'organization.deleted': 'organizations',
  'job_role.created': 'resources',
  'job_role.updated': 'resources',
  'job_role.deleted': 'resources',
  'user.job_roles_changed': 'resources',
  'area.created': 'resources',
  'area.updated': 'resources',
  'area.deleted': 'resources',
  'skill.created': 'resources',
  'skill.updated': 'resources',
  'skill.deleted': 'resources',
  'user.skills_changed': 'resources',
  'user.holiday_region_changed': 'resources',
  'user.bookable_percent_changed': 'resources',
  'user.work_profile_created': 'resources',
  'user.work_profile_updated': 'resources',
  'user.work_profile_deleted': 'resources',
  'user.absence_created': 'resources',
  'user.absence_updated': 'resources',
  'user.absence_deleted': 'resources',
  'project_template.saved': 'system',
  'project_template.updated': 'system',
  'project_template.deleted': 'system',
  'settings.updated': 'system',
  'security.alert_sent': 'system',
  'audit.exported': 'system',
  'audit.purged': 'system',
} as const satisfies Record<string, AuditCategory>;

export const auditCategories = [
  'auth',
  'users',
  'rights',
  'organizations',
  'resources',
  'system',
] as const;
export type AuditCategory = (typeof auditCategories)[number];

export type AuditAction = keyof typeof auditActionCatalog;

export const auditActions = Object.keys(auditActionCatalog) as [AuditAction, ...AuditAction[]]; // keys of a non-empty literal catalog

export function isAuditAction(action: string): action is AuditAction {
  return Object.hasOwn(auditActionCatalog, action);
}

export function auditActionsOfCategory(category: AuditCategory): AuditAction[] {
  return auditActions.filter((action) => auditActionCatalog[action] === category);
}

export const auditEntityTypes = [
  'user',
  'organization',
  'role',
  'project',
  'job_role',
  'area',
  'skill',
  'setting',
  'project_template',
] as const;
export type AuditEntityType = (typeof auditEntityTypes)[number];

/** Entries older than this are deleted by the nightly maintenance job (E-28). */
export const AUDIT_RETENTION_MONTHS = 12;

/** Upper bound of one CSV export; narrow the filters for more. */
export const AUDIT_EXPORT_MAX_ROWS = 50_000;

/** Filters of the audit view and its CSV export (ADM-05). Dates are Europe/Berlin days. */
export const auditFilterSchema = z.object({
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  category: z.enum(auditCategories).optional(),
  action: z.enum(auditActions).optional(),
  /** Acting or affected user. */
  userId: z.uuid().optional(),
  highlighted: z.stringbool().optional(),
  /** E-mail in the details or IP address, substring. */
  q: z.string().trim().max(200).optional(),
});
export type AuditFilter = z.infer<typeof auditFilterSchema>;

/** The cursor is the id of the last entry of the previous page. */
export const auditListQuerySchema = cursorQuerySchema.extend({
  ...auditFilterSchema.shape,
  cursor: z.uuid().optional(),
});
export type AuditListQuery = z.infer<typeof auditListQuerySchema>;

const auditPersonSchema = z.object({ id: z.uuid(), name: z.string(), email: z.string() });

export const auditEntrySchema = z.object({
  id: z.uuid(),
  createdAt: timestampSchema,
  action: z.enum(auditActions),
  category: z.enum(auditCategories),
  /** null: system, CLI or anonymous (e.g. failed sign-in). */
  actor: auditPersonSchema.nullable(),
  entityType: z.enum(auditEntityTypes).nullable(),
  entityId: z.uuid().nullable(),
  /** Name of the affected user or organization, when it still exists. */
  entityName: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  highlight: z.boolean(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const auditListResponseSchema = pageSchema(auditEntrySchema);
export type AuditListResponse = z.infer<typeof auditListResponseSchema>;

/** German names for the audit view and the CSV export (like the labels of the permission catalog). */
export const auditCategoryLabels: Record<AuditCategory, string> = {
  auth: 'Anmeldung',
  users: 'Benutzer',
  rights: 'Rechte',
  organizations: 'Organisationen',
  resources: 'Ressourcen',
  system: 'System',
};

export const auditActionLabels: Record<AuditAction, string> = {
  'auth.login_succeeded': 'Anmeldung erfolgreich',
  'auth.login_failed': 'Anmeldung fehlgeschlagen',
  'auth.login_blocked': 'Anmeldung gesperrt (zu viele Fehlversuche)',
  'auth.logout': 'Abmeldung',
  'auth.password_reset_requested': 'Passwort-Reset angefordert',
  'auth.password_reset_completed': 'Passwort zurückgesetzt',
  'auth.two_factor_enabled': 'Zwei-Faktor-Anmeldung eingerichtet',
  'auth.two_factor_reset': 'Zwei-Faktor-Anmeldung zurückgesetzt',
  'auth.backup_codes_regenerated': 'Wiederherstellungscodes neu erzeugt',
  'auth.sessions_revoked': 'Sitzungen beendet',
  'auth.entra_linked': 'Microsoft-Konto verknüpft',
  'auth.entra_rejected': 'Microsoft-Anmeldung abgelehnt',
  'user.invited': 'Person eingeladen',
  'user.invitation_accepted': 'Einladung angenommen',
  'user.provisioned': 'Person bei erster Microsoft-Anmeldung angelegt',
  'user.created_internal': 'Interne Person vorab angelegt',
  'user.entra_profile_updated': 'Name/E-Mail aus Microsoft übernommen',
  'user.deactivated': 'Person deaktiviert',
  'user.activated': 'Person aktiviert',
  'user.avatar_removed': 'Profilbild entfernt',
  'user.break_glass_created': 'Notfallkonto angelegt',
  'user.break_glass_reset': 'Notfallkonto zurückgesetzt',
  'user.global_roles_changed': 'Globale Rollen geändert',
  'role.created': 'Rolle angelegt',
  'role.updated': 'Rolle geändert',
  'role.deleted': 'Rolle gelöscht',
  'project.member_added': 'Projektmitglied hinzugefügt',
  'project.member_role_changed': 'Projektrolle geändert',
  'project.member_removed': 'Projektmitglied entfernt',
  'organization.created': 'Organisation angelegt',
  'organization.updated': 'Organisation geändert',
  'organization.deleted': 'Organisation gelöscht',
  'job_role.created': 'Fachliche Rolle angelegt',
  'job_role.updated': 'Fachliche Rolle geändert',
  'job_role.deleted': 'Fachliche Rolle gelöscht',
  'user.job_roles_changed': 'Fachliche Rollen einer Person geändert',
  'area.created': 'Bereich angelegt',
  'area.updated': 'Bereich geändert',
  'area.deleted': 'Bereich gelöscht',
  'skill.created': 'Skill angelegt',
  'skill.updated': 'Skill geändert',
  'skill.deleted': 'Skill gelöscht',
  'user.skills_changed': 'Skills einer Person geändert',
  'user.holiday_region_changed': 'Feiertagsregion geändert',
  'user.bookable_percent_changed': 'Buchbarer Anteil geändert',
  'user.work_profile_created': 'Arbeitszeitprofil angelegt',
  'user.work_profile_updated': 'Arbeitszeitprofil geändert',
  'user.work_profile_deleted': 'Arbeitszeitprofil gelöscht',
  'user.absence_created': 'Abwesenheit eingetragen',
  'user.absence_updated': 'Abwesenheit geändert',
  'user.absence_deleted': 'Abwesenheit gelöscht',
  'project_template.saved': 'Projektvorlage gespeichert',
  'project_template.updated': 'Projektvorlage geändert',
  'project_template.deleted': 'Projektvorlage gelöscht',
  'settings.updated': 'Einstellungen geändert',
  'security.alert_sent': 'Sicherheitswarnung versandt',
  'audit.exported': 'Audit-Log exportiert',
  'audit.purged': 'Alte Audit-Einträge gelöscht',
};

/** Reasons in `details.reason` of failed or rejected sign-ins. */
export const auditReasonLabels: Record<string, string> = {
  unknown_user: 'unbekannte E-Mail-Adresse',
  not_allowed: 'Anmeldung mit Passwort nicht erlaubt',
  invalid_password: 'falsches Passwort',
  invalid_totp: 'falscher Bestätigungscode',
  invalid_backup_code: 'falscher Wiederherstellungscode',
  invalid_token: 'ungültiges Token',
  foreign_tenant: 'fremder Microsoft-Tenant',
  missing_email: 'keine E-Mail-Adresse im Microsoft-Konto',
  external_account: 'E-Mail gehört zu einem Kundenkonto',
  object_id_mismatch: 'Konto ist mit einem anderen Microsoft-Konto verknüpft',
  email_conflict: 'neue E-Mail gehört einer anderen Person',
  account_inactive: 'Konto deaktiviert',
};
